import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { routeTree } from './routeTree.gen'
import './index.css'

// Create a new router instance
const router = createRouter({ routeTree, scrollRestoration: true })

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Persist UTM parameters for marketing tracking
if (typeof window !== 'undefined') {
  const urlParams = new URLSearchParams(window.location.search);
  const utms = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref', 'gclid', 'fbclid'];
  const currentUtms: Record<string, string> = {};
  let hasUtms = false;
  
  utms.forEach(param => {
    if (urlParams.has(param)) {
      const val = urlParams.get(param);
      if (val) currentUtms[param] = val;
      hasUtms = true;
    }
  });

  if (hasUtms) {
    localStorage.setItem('wb_utms', JSON.stringify(currentUtms));
  }
}

// Initialize Sentry
const SENTRY_DSN = 'https://05d513e96a5dd23d9b1dac2b71b0be93@o4508304578904064.ingest.us.sentry.io/4508304583163904'
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || 'dev'

Sentry.init({
  dsn: SENTRY_DSN,
  environment: import.meta.env.MODE || 'production',
  release: SENTRY_RELEASE,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: false,
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn', 'info'] // Captures frontend console logs automatically
    })
  ],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  _experiments: {
    metricsAggregator: true
  },
})


// Initialize PostHog

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://n.winboxzero.com'

if (typeof window !== 'undefined' && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    loaded: (ph) => {
      Sentry.setTag("posthog_session_id", ph.get_session_id());
    }
  })

  // Track page views with React Router
  router.subscribe('onResolved', () => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
    });
  });
}

const container = document.getElementById('root')!;
const root = ReactDOM.createRoot(container, {
  onCaughtError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.error('Uncaught error:', error, errorInfo);
  }),
  onRecoverableError: Sentry.reactErrorHandler(),
});

// Send an initial load metric
Sentry.metrics.count('app_loaded', 1);

root.render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <RouterProvider router={router} />
    </PostHogProvider>
  </React.StrictMode>,
);
